import Vue from "vue";
import { mapGetters } from "vuex";

import buttonScComponent from "@/components/child/form/buttonSc.vue";
import checkboxComponent from "@/components/child/form/checkbox.vue";
import comboComponent from "@/components/child/form/combo.vue";
import decimalComponent from "@/components/child/form/decimal.vue";
import moedaComponent from "@/components/child/form/moeda.vue";
import { Component, Prop, Watch } from "@/decorators";
import TiposCalculoManipulacao from "@/models/enum/tiposCalculoManipulacao";
import TiposItemManipulacao from "@/models/enum/tiposItemManipulacao";
import ManipulacaoOrdemItemModel from "@/models/manipulacaoOrdemItemModel";
import {
    ItemTableType,
    ManipulacaoContextNewActions,
    ManipulacaoContextNewMutations,
    ManipulacaoContextNewState,
} from "@/store/manipulacaoContextNew/types";
import { Getters } from "@/store/store";
import Delay from "@/utils/common/delay";

import ManipulacaoItemComponent from "../item";
import manipulacaoItemComponent from "../item.vue";

@Component({
    components: {
        comboComponent,
        checkboxComponent,
        moedaComponent,
        decimalComponent,
        manipulacaoItemComponent,
        buttonScComponent,
    },
    computed: {
        ...mapGetters(["GET_MANIPULACAO_CONTEXT_NEW"] as Getters),
    },
})
export default class ManipulacaoItensAtivosComponent extends Vue {
    // State computed props
    GET_MANIPULACAO_CONTEXT_NEW!: (uuid: string) => ManipulacaoContextNewState;

    @Prop({ type: String, required: true }) uuid!: string;
    @Prop({ type: Number, default: 0 }) setorId!: number;

    private theader: string[] = [];
    private theader2: string[] = [];
    private theaderColSpan: number[] = [];
    private theaderColSpan2: number[] = [];
    private itensReference: ManipulacaoItemComponent[] = [];
    private manipulacaoContext = new ManipulacaoContextNewState();
    private unsubscribe: (() => void) | null = null;
    private unsubscribeActions: (() => void) | null = null;

    public clear() {
        this.onRefazerHeader();

        for (const item of this.itensReference) {
            item.clear();
        }
    }

    public updateDesmembrado(produtosDesmembrados: ManipulacaoOrdemItemModel[]) {
        this.itensReference[0].updateDesmembrado(produtosDesmembrados);
    }

    public async clearItensAssociados() {
        this.onRefazerHeader();

        await this.loadItensReference();
    }

    public async validateAll() {
        for (const item of this.itensReference) {
            const isValid = await item.validateAll();
            if (!isValid) {
                return false;
            }
        }
        return true;
    }

    public addEmptyRow() {
        this.addNewRow(null);
    }

    private onAdicionarFormulaBase() {
        const itemModel = new ManipulacaoOrdemItemModel();
        itemModel.tipoItem = TiposItemManipulacao.FormulaPadrao;

        this.addNewRow(itemModel);
    }

    private async addNewRow(item: ManipulacaoOrdemItemModel | null) {
        await this.dispatch(ManipulacaoContextNewActions.ADD_ITEM_MANIPULACAO_NEW, { item, type: ItemTableType.ATIVO });

        await this.loadItensReference();

        this.onRefazerHeader();
    }

    private async removeItem(index: number) {
        this.dispatch(ManipulacaoContextNewActions.REMOVE_ITEM_MANIPULACAO_NEW, { type: ItemTableType.ATIVO, index });

        this.onRefazerHeader();

        await this.dispatch(ManipulacaoContextNewActions.CALCULAR_QSP_NEW, {
            loadDadosLote: false,
            ignoreDebounce: false,
        });
    }

    private async onSetAtivoPrincipal(index: number) {
        await this.dispatch(ManipulacaoContextNewActions.SET_ATIVO_PRINCIPAL_NEW, index);
    }

    private onRefazerHeader() {
        this.theader = [];
        this.theaderColSpan = [];
        this.theader2 = [];
        this.theaderColSpan2 = [];
        this.theader.push(this.$t("__.ts.prod") as string);
        this.theader.push(this.$t("__.ts.calc") as string);
        if (this.manipulacaoContext.ativos.some(p => p.tipoCalculo == TiposCalculoManipulacao.Dose)) {
            this.theader.push(this.$t("__.ts.volDose") as string);
            this.theaderColSpan = [4, 1, 1, 2, 1];
        } else {
            this.theaderColSpan = [4, 2, 2, 1];
        }
        this.theader.push(this.$t("__.ts.concentr") as string);
        this.theader.push(this.$t("__.ts.unMedida") as string);

        this.theader2 = [this.$t("__.ts.qtdTotal") as string, this.$t("__.ts.total") as string];
        this.theaderColSpan2 = [6, 6];
    }

    private onAlterarVolumeCapsula(volume: number, descricao: string) {
        this.$emit("onAlterarVolumeCapsula", volume, descricao);
    }

    private onCorCapsulaChange(model: ManipulacaoOrdemItemModel) {
        this.$emit("onCorCapsulaChange", model);
    }

    private onConsolidado(consolidado: boolean) {
        this.$emit("onConsolidado", consolidado);
    }

    @Watch("formaFarmaceuticaId")
    private async onChangedFormaFarmaceutica() {
        this.loadManipulacaoContext();
    }

    private async loadItensReference() {
        this.itensReference = [];

        if (this.manipulacaoContext.ativos.length > 0) {
            while (!this.$refs.manipulacaoItemComponent) await Delay(100);
            const refs = this.$refs.manipulacaoItemComponent as ManipulacaoItemComponent[];
            for (let index = 0; index < refs.length; index++) {
                this.itensReference[index] = refs[index];
            }
        }
    }

    public forceUpdate() {
        this.$forceUpdate();

        this.loadItensReference();

        for (const item of this.itensReference) {
            item.forceUpdate();
        }
    }

    private async dispatch(action: ManipulacaoContextNewActions, data: unknown) {
        await this.$store.dispatch(action, { uuid: this.uuid, data });

        this.loadManipulacaoContext();
    }

    public refreshManipulacaoContext() {
        this.loadManipulacaoContext();

        for (const item of this.itensReference) {
            item.loadManipulacaoContext();
        }
    }

    private loadManipulacaoContext() {
        this.manipulacaoContext = this.GET_MANIPULACAO_CONTEXT_NEW(this.uuid);
    }

    public unsubscribeAll() {
        this.unsubscribe?.();
        this.unsubscribeActions?.();

        for (const item of this.itensReference) {
            item.unsubscribeAll();
        }
    }

    private remontarFormulaPadraoDesmembrada() {
        this.$forceUpdate();

        this.loadItensReference();

        for (const item of this.itensReference.filter(p => p.isFormulaPadrao)) {
            item.remontarFormulaPadraoDesmembrada();
        }
    }

    private async mounted() {
        this.unsubscribe = this.$store.subscribe(mutation => {
            switch (mutation.type) {
                case ManipulacaoContextNewMutations.ADD_ITEM_MANIPULACAO_NEW:
                case ManipulacaoContextNewMutations.REMOVE_ALL_ITENS_BY_TYPE_NEW:
                case ManipulacaoContextNewMutations.REMOVE_ITEM_MANIPULACAO_NEW:
                case ManipulacaoContextNewMutations.UPDATE_MANIPULACAO_NEW:
                case ManipulacaoContextNewMutations.REMOVE_EMBALAGENS_BY_KIT_ID_NEW:
                case ManipulacaoContextNewMutations.REMOVE_ATIVOS_ASSOCIADOS_NEW:
                case ManipulacaoContextNewMutations.CONSOLIDAR_NEW:
                    this.$forceUpdate();
                    this.loadManipulacaoContext();
                    break;
                case ManipulacaoContextNewMutations.CALCULAR_QSP_NEW:
                    this.$forceUpdate();
                    this.loadManipulacaoContext();
                    this.remontarFormulaPadraoDesmembrada();
                    break;
            }
        });

        this.unsubscribeAll = this.$store.subscribeAction({
            after: action => {
                switch (action.type) {
                    case ManipulacaoContextNewActions.ADD_ITEM_MANIPULACAO_NEW:
                    case ManipulacaoContextNewActions.CALCULAR_QSP_NEW:
                    case ManipulacaoContextNewActions.REMOVE_ATIVOS_ASSOCIADOS_NEW:
                        this.$forceUpdate();
                        break;
                }
            },
        });

        this.onRefazerHeader();
        this.loadManipulacaoContext();
    }
}
